import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Loader2, ChevronLeft, Building2, FileText, MonitorSmartphone } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import LoadingNoteWizard from './LoadingNoteWizard';
import PartySelector from './PartySelector';
import DriverFlow from './flows/DriverFlow';
import SiteRepFlow from './flows/SiteRepFlow';
import PaperBonFlow from './flows/PaperBonFlow';
import BaobabOperatorReviewFlow from './flows/BaobabOperatorReviewFlow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PM Spec V3 13/05/2026 — LoadingNoteSessionHub
 *
 * Pivot orchestrateur des 3 modes de signature :
 *   - distributed : 1 lien WhatsApp ciblé → 1 acteur signe sa partie
 *                   (partyScope fixé via URL, pas de menu)
 *   - tablet      : 1 tablette, plusieurs acteurs → menu PartySelector
 *                   (admin connecté ou chauffeur restreint)
 *   - expert      : admin backoffice → wizard 5 étapes complet (legacy)
 *
 * Props :
 *   mode: 'distributed' | 'tablet' | 'expert'
 *   noteType: 'loading' | 'unloading' (figé en distributed via JWT noteType)
 *   operation, operatorName: contexte opération
 *   endpointPrefix: ex `${API}/public/sign/{opId}/loading-notes`
 *                   ou `${API}/operations/{opId}/loading-notes`
 *   authHeaders: { Authorization: 'Bearer ...' }
 *   partyScope: party fixée (mode distributed)
 *   linkId: id du lien (mode distributed)
 *   sessionRole: 'admin' | 'driver' | 'distributed'
 *                contrôle l'accès dans PartySelector
 *   onCompleted: callback après signature
 *
 * En P3, le Hub route vers `LoadingNoteWizard` existant (mode expert).
 * En P4, il routera vers DriverFlow / SiteRepFlow / BaobabOperatorReviewFlow
 * selon `partyScope` + `uiLevel`.
 */
const LoadingNoteSessionHub = ({
  mode = 'distributed',
  noteType,
  operation,
  operatorName,
  endpointPrefix,
  authHeaders,
  partyScope = null,
  linkId = null,
  noteId = null,
  sessionRole = 'distributed',
  onCompleted,
}) => {
  const [statusV3, setStatusV3] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [selectedParty, setSelectedParty] = useState(partyScope || null);
  const [selectedNoteType, setSelectedNoteType] = useState(noteType || null);
  // PM Spec V3 — Canal pour le chauffeur : 'digital' ou 'paper'
  const [driverChannel, setDriverChannel] = useState(null);

  // Pour le mode tablette, on charge le status V3 pour connaître les liens dispo
  const isTablet = mode === 'tablet';

  const fetchStatus = useCallback(async () => {
    if (!isTablet || !operation?.id) return;
    setLoadingStatus(true);
    try {
      const adminPrefix = endpointPrefix.replace(/\/loading-notes\/?$/, '/loading-notes');
      const r = await axios.get(`${adminPrefix}/_signatures-status`, { headers: authHeaders });
      setStatusV3(r.data || null);
    } catch (err) {
      // Non-bloquant : on log seulement
      // eslint-disable-next-line no-console
      console.warn('Status V3 fetch failed', err);
    } finally {
      setLoadingStatus(false);
    }
  }, [isTablet, operation?.id, endpointPrefix, authHeaders]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // PM Bug fix 17/02/2026 — `useMemo` MUST be called before any early return (rules-of-hooks).
  // Calcule les parties dispo pour le bon courant (utilisé en mode tablet uniquement).
  const partiesForNote = useMemo(() => {
    if (!statusV3) return [];
    const noteStatus = selectedNoteType === 'loading' ? statusV3.loadingNote : statusV3.unloadingNote;
    if (!noteStatus) return [];
    return (noteStatus.parties || []).map((p) => ({
      party: p.party,
      signed: p.signed,
      linkState: p.linkState,
      linkId: p.linkId,
      preFillName: p.preFillName || p.targetName || null,
      // Restriction d'accès en mode tablette :
      //   admin → tout autorisé
      //   driver → uniquement sa propre partie (driver)
      allowed: sessionRole === 'admin' || (sessionRole === 'driver' && p.party === 'driver'),
    }));
  }, [statusV3, selectedNoteType, sessionRole]);

  // ─── Helper : route vers le flow simplifié selon party + canal ───
  // En mode tablet, on intercepte onCompleted pour revenir au selector.
  const flowOnCompleted = (...args) => {
    if (isTablet) {
      setSelectedParty(null);
      setDriverChannel(null);
      fetchStatus();
      toast({ title: 'Signature enregistrée', description: 'Passez au signataire suivant.' });
    }
    onCompleted?.(...args);
  };

  const renderFlow = (party, noteTypeFinal, preFillName) => {
    if (!party || !noteTypeFinal) return null;

    // SHIPPER / CONSIGNEE → SiteRepFlow (3 écrans illettré-friendly)
    if (party === 'shipper' || party === 'consignee') {
      return (
        <SiteRepFlow
          type={noteTypeFinal}
          party={party}
          operation={operation}
          operatorName={preFillName || operatorName}
          endpointPrefix={endpointPrefix}
          authHeaders={authHeaders}
          linkId={linkId}
          noteId={noteId}
          onCompleted={flowOnCompleted}
        />
      );
    }
    // DRIVER → choix canal : digital flow OU paper flow
    if (party === 'driver') {
      if (driverChannel === 'paper') {
        return (
          <PaperBonFlow
            type={noteTypeFinal}
            party={noteTypeFinal === 'loading' ? 'shipper' : 'consignee'}
            operation={operation}
            operatorName={operatorName}
            preFillName={preFillName}
            endpointPrefix={endpointPrefix}
            authHeaders={authHeaders}
            linkId={linkId}
            noteId={noteId}
            onCompleted={flowOnCompleted}
          />
        );
      }
      return (
        <DriverFlow
          type={noteTypeFinal}
          operation={operation}
          operatorName={operatorName}
          endpointPrefix={endpointPrefix}
          authHeaders={authHeaders}
          linkId={linkId}
          noteId={noteId}
          onCompleted={flowOnCompleted}
        />
      );
    }
    // BAOBAB_OPERATOR → vue de validation à distance (V3)
    if (party === 'baobab_operator') {
      return (
        <BaobabOperatorReviewFlow
          type={noteTypeFinal}
          operation={operation}
          operatorName={operatorName}
          endpointPrefix={endpointPrefix}
          authHeaders={authHeaders}
          linkId={linkId}
          noteId={noteId}
          onCompleted={flowOnCompleted}
        />
      );
    }
    // OBSERVER / autres → fallback wizard
    return (
      <LoadingNoteWizard
        key={`${noteTypeFinal}-${party}`}
        type={noteTypeFinal}
        operation={operation}
        operatorName={operatorName}
        endpointPrefix={endpointPrefix}
        authHeaders={authHeaders}
        partyScope={party}
        linkId={linkId}
        noteId={noteId}
        onCompleted={flowOnCompleted}
      />
    );
  };

  // ─── Sélecteur de canal pour DRIVER (avant le flow) ───
  const renderDriverChannelChoice = () => (
    <Card data-testid="hub-driver-channel-choice">
      <CardContent className="p-5 space-y-4">
        <div className="space-y-1">
          <Badge className="bg-emerald-600 text-white border-0">Choisissez le mode</Badge>
          <div className="text-base font-semibold text-slate-900">
            Comment voulez-vous procéder ?
          </div>
        </div>
        <div className="grid gap-3">
          <button
            type="button"
            data-testid="hub-driver-channel-digital"
            onClick={() => setDriverChannel('digital')}
            className="text-left rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 p-4 transition-all flex items-center gap-3 active:scale-[0.99]"
          >
            <MonitorSmartphone className="w-10 h-10 text-emerald-700 shrink-0" />
            <div>
              <div className="font-semibold text-slate-900">Signer sur l'écran</div>
              <div className="text-sm text-slate-600">Le client signe directement sur cette tablette</div>
            </div>
          </button>
          <button
            type="button"
            data-testid="hub-driver-channel-paper"
            onClick={() => setDriverChannel('paper')}
            className="text-left rounded-xl border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 p-4 transition-all flex items-center gap-3 active:scale-[0.99]"
          >
            <FileText className="w-10 h-10 text-amber-700 shrink-0" />
            <div>
              <div className="font-semibold text-slate-900">Photographier le bon papier</div>
              <div className="text-sm text-slate-600">Le bon est déjà signé sur papier</div>
            </div>
          </button>
        </div>
      </CardContent>
    </Card>
  );

  // ─── Mode distributed (1 lien WhatsApp ciblé) ─────────────
  if (mode === 'distributed') {
    if (!selectedNoteType) {
      return (
        <div className="text-center py-6 text-slate-500 text-sm" data-testid="hub-no-type">
          Type de bon non spécifié.
        </div>
      );
    }
    // Driver : choix canal d'abord
    if (selectedParty === 'driver' && !driverChannel) {
      return renderDriverChannelChoice();
    }
    // partyScope donné par URL → on lance le flow direct
    if (selectedParty) {
      return renderFlow(selectedParty, selectedNoteType, null);
    }
    // Pas de partyScope (ancien lien sans party) → fallback wizard expert
    return (
      <LoadingNoteWizard
        key={selectedNoteType}
        type={selectedNoteType}
        operation={operation}
        operatorName={operatorName}
        endpointPrefix={endpointPrefix}
        authHeaders={authHeaders}
        partyScope={null}
        linkId={linkId}
        onCompleted={onCompleted}
      />
    );
  }

  // ─── Mode expert (admin backoffice) ───────────────────────
  if (mode === 'expert') {
    if (!selectedNoteType) {
      return (
        <div className="text-center py-6 text-slate-500 text-sm" data-testid="hub-no-type">
          Type de bon non spécifié.
        </div>
      );
    }
    return (
      <LoadingNoteWizard
        key={`${selectedNoteType}-expert`}
        type={selectedNoteType}
        operation={operation}
        operatorName={operatorName}
        endpointPrefix={endpointPrefix}
        authHeaders={authHeaders}
        partyScope={partyScope}
        linkId={linkId}
        onCompleted={onCompleted}
      />
    );
  }

  // Mode tablet : PartySelector → wizard
  // (partiesForNote calculé en haut du composant pour respecter rules-of-hooks)

  // Si aucune note sélectionnée → choix BC/BD
  if (!selectedNoteType) {
    return (
      <div className="space-y-4" data-testid="hub-tablet-note-choice">
        <div className="text-base font-semibold text-slate-900">
          Quel bon souhaitez-vous traiter ?
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button
            data-testid="hub-pick-loading"
            onClick={() => setSelectedNoteType('loading')}
            variant="outline"
            className="h-24 flex flex-col items-center justify-center gap-1 border-emerald-200 hover:border-emerald-500 hover:bg-emerald-50"
          >
            <span className="text-lg font-semibold text-emerald-700">Bon de chargement</span>
            <span className="text-xs text-slate-500">Site de départ</span>
          </Button>
          <Button
            data-testid="hub-pick-unloading"
            onClick={() => setSelectedNoteType('unloading')}
            variant="outline"
            className="h-24 flex flex-col items-center justify-center gap-1 border-emerald-200 hover:border-emerald-500 hover:bg-emerald-50"
          >
            <span className="text-lg font-semibold text-emerald-700">Bon de déchargement</span>
            <span className="text-xs text-slate-500">Site de livraison</span>
          </Button>
        </div>
      </div>
    );
  }

  // Si party non sélectionnée → afficher le PartySelector
  if (!selectedParty) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost" size="sm"
            data-testid="hub-back-to-note-choice"
            onClick={() => setSelectedNoteType(null)}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Changer de bon
          </Button>
          {sessionRole === 'admin' && (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Building2 className="w-3 h-3" /> Mode tablette présence
            </span>
          )}
        </div>
        {loadingStatus ? (
          <div className="text-center py-6 text-slate-500 text-sm" data-testid="hub-loading-status">
            <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Chargement…
          </div>
        ) : (
          <PartySelector
            noteType={selectedNoteType}
            parties={partiesForNote}
            currentSession={sessionRole}
            onSelect={(party) => setSelectedParty(party)}
            testId="hub-party-selector"
          />
        )}
      </div>
    );
  }

  // Party sélectionnée → renvoie au flow simplifié (Driver/SiteRep) ou wizard
  return (
    <div className="space-y-3">
      <Button
        variant="ghost" size="sm"
        data-testid="hub-back-to-party-selector"
        onClick={() => { setSelectedParty(null); setDriverChannel(null); fetchStatus(); }}
      >
        <ChevronLeft className="w-4 h-4 mr-1" /> Retour au choix du signataire
      </Button>
      {selectedParty === 'driver' && !driverChannel
        ? renderDriverChannelChoice()
        : renderFlow(selectedParty, selectedNoteType, null)}
    </div>
  );
};

export default LoadingNoteSessionHub;
